import React, { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Input,
  Select,
  Space,
  Typography,
  Badge,
  Spin,
  Button,
  Modal,
  Form,
  InputNumber,
  Table,
  Tag,
  Divider,
  Radio,
  DatePicker,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  SendOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { toast } from "react-toastify";
import moment from "moment";
import {
  getDefaultFlow,
  getSchedulableJobs,
} from "../../api/productionApi";
import { moveJobToStage } from "../../api/jobApi";
import { getBatchesForKanban } from "../../api/batchPlanningApi";
import { getReadyForDispatch, createDispatch } from "../../api/dispatchApi";
import KanbanColumn from "./KanbanColumn";
import KanbanCard from "./KanbanCard";
import JobDetail from "../job/JobDetail";
import PageTitle from "../page-header/PageHeader";
import checkTokenExp from "../../utils/checkTokenExp";
import "./Kanban.css";

const { Title, Text } = Typography;
const { Search } = Input;
const { confirm } = Modal;

const KanbanBoard = () => {
  // ── Auth ───────────────────────────────────────────────────
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  const accessToken = localStorage.getItem("access-token");

  // ── State ──────────────────────────────────────────────────
  const [stages, setStages] = useState([]); // dynamic columns from flow
  const [columns, setColumns] = useState({}); // { [stageCode]: Job[] }
  const [loading, setLoading] = useState(true);
  const [activeItem, setActiveItem] = useState(null);
  const [flowName, setFlowName] = useState("");

  const [filters, setFilters] = useState({
    search: "",
    priority: undefined,
    fulfillment_status: undefined,
  });

  // Job detail modal
  const [jobModalId, setJobModalId] = useState(null);

  // Track the original column when dragging starts
  const [dragFromContainer, setDragFromContainer] = useState(null);

  // Dispatch modal state
  const [dispatchModalVisible, setDispatchModalVisible] = useState(false);
  const [dispatchData, setDispatchData] = useState(null); // { customers: [...] }
  const [dispatchLoading, setDispatchLoading] = useState(false);
  const [dispatchForm] = Form.useForm();
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [dispatchItems, setDispatchItems] = useState([]); // [{ job_id, qty_dispatched }]

  // Stage completion modal state
  const [stageFormVisible, setStageFormVisible] = useState(false);
  const [pendingMove, setPendingMove] = useState(null); // { job, from, to, skippedStages }
  const [stageForm] = Form.useForm();

  // Production stages that require completion form
  const PRODUCTION_STAGES = [
    "pre_print", "coating", "corrugation", "sheet_cutting",
    "pasting", "casting", "die_cutting", "joining", "after_box_print", "qc",
  ];

  // ── Sensors ────────────────────────────────────────────────
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // ── Data Loading ───────────────────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      // 1) Get the default production flow → stages become columns
      const flow = await getDefaultFlow();
      const flowStages = (flow.stages || []).sort(
        (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)
      );
      setStages(flowStages);
      setFlowName(flow.name || "Production Kanban");

      // 2) Build query params from filters
      const params = {};
      if (filters.priority) params.priority = filters.priority;
      if (filters.fulfillment_status)
        params.fulfillment_status = filters.fulfillment_status;

      // 3) Fetch schedulable jobs for each stage + locked batch IDs in parallel
      const [jobResults, batchKanban] = await Promise.all([
        Promise.allSettled(flowStages.map((stage) => getSchedulableJobs(stage.code, params))),
        getBatchesForKanban().catch(() => ({})),
      ]);

      // Build a set of batch_ids that are currently locked
      const lockedBatchIds = new Set();
      Object.values(batchKanban).forEach((batches) => {
        batches.forEach((b) => { if (b.is_locked) lockedBatchIds.add(b.id); });
      });

      const cols = {};
      flowStages.forEach((stage, idx) => {
        const result = jobResults[idx];
        let jobs =
          result.status === "fulfilled" ? result.value.jobs || [] : [];

        // Stamp _batchLocked on jobs whose batch is locked
        jobs = jobs.map((j) => ({
          ...j,
          _batchLocked: j.batch_id ? lockedBatchIds.has(j.batch_id) : false,
        }));

        // Client-side search filter
        if (filters.search) {
          const q = filters.search.toLowerCase();
          jobs = jobs.filter(
            (j) =>
              (j.job_number || "").toLowerCase().includes(q) ||
              (j.item_name || "").toLowerCase().includes(q) ||
              (j.order?.order_number || "").toLowerCase().includes(q)
          );
        }

        cols[stage.code] = jobs;
      });

      setColumns(cols);
    } catch (error) {
      console.log(error.message);
      toast.error("Failed to load kanban data");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // ── Auth guard (after hooks) ───────────────────────────────
  if (!isLogged) {
    return <Navigate to={"/auth/login"} replace={true} />;
  }
  checkTokenExp(accessToken);

  // ── Drag helpers ───────────────────────────────────────────
  const findContainer = (id) => {
    // If the id IS a column key, return it
    if (id in columns) return id;

    // Otherwise find which column contains a job with this id
    for (const [code, jobs] of Object.entries(columns)) {
      if (jobs.find((j) => j.id === id)) return code;
    }
    return null;
  };

  const handleDragStart = (event) => {
    const container = findContainer(event.active.id);
    if (container) {
      const job = columns[container].find((j) => j.id === event.active.id);
      if (job?._batchLocked) {
        toast.warning(`${job.job_number} is locked in batch ${job.batch_number}. Unlock the batch first.`);
        return;
      }
      setDragFromContainer(container);
      setActiveItem(job || null);
    }
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;

    const from = findContainer(active.id);
    const to = findContainer(over.id) || over.id; // over.id may be column id

    if (!from || !to || from === to) return;

    // Optimistic UI: move the card across columns
    setColumns((prev) => {
      const fromJobs = [...(prev[from] || [])];
      const toJobs = [...(prev[to] || [])];
      const idx = fromJobs.findIndex((j) => j.id === active.id);
      if (idx === -1) return prev;

      const [moved] = fromJobs.splice(idx, 1);
      return { ...prev, [from]: fromJobs, [to]: [...toJobs, moved] };
    });
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveItem(null);
    const from = dragFromContainer;
    setDragFromContainer(null);
    if (!over || !from) return;

    const to = findContainer(over.id) || over.id;

    // Same column → reorder (purely cosmetic, no API call)
    if (from === to) {
      const jobs = columns[from];
      const oldIndex = jobs.findIndex((j) => j.id === active.id);
      const newIndex = jobs.findIndex((j) => j.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        setColumns((prev) => ({
          ...prev,
          [from]: arrayMove(prev[from], oldIndex, newIndex),
        }));
      }
      return;
    }

    // Different column → move job to the target stage
    const job = columns[to]?.find((j) => j.id === active.id);
    if (!job) return;

    const targetStage = stages.find((s) => s.code === to);
    const fromStage = stages.find((s) => s.code === from);

    // Determine skipped stages for confirmation
    const fromIdx = stages.findIndex((s) => s.code === from);
    const toIdx = stages.findIndex((s) => s.code === to);
    const skippedStages = toIdx > fromIdx + 1
      ? stages.slice(fromIdx + 1, toIdx).map((s) => s.name)
      : [];

    // ── DISPATCH TRIGGER: ready_for_dispatch → dispatched ──
    if (to === "dispatched" || (from === "ready_for_dispatch" && to === "dispatched")) {
      // Revert optimistic move, open dispatch modal instead
      fetchData();
      openDispatchModal();
      return;
    }

    // ── STAGE COMPLETION FORM: leaving a production stage ──
    if (PRODUCTION_STAGES.includes(from)) {
      setPendingMove({ job, from, to, fromStage, targetStage, skippedStages });
      stageForm.resetFields();
      stageForm.setFieldsValue({ output_qty: job.ordered_qty || 0 });
      setStageFormVisible(true);
      return;
    }

    // ── NORMAL MOVE (non-production stages) ──
    const doMove = async (stageCompletion = null) => {
      try {
        const result = await moveJobToStage(job.id, to, stageCompletion);
        toast.success(`${job.job_number} → ${targetStage?.name || to}`);
        if (result.skipped_stages?.length > 0) {
          toast.info(`Skipped: ${skippedStages.join(", ")}`);
        }
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to move job");
        fetchData(); // revert
      }
    };

    // If skipping stages, confirm
    if (skippedStages.length > 0) {
      confirm({
        title: `Skip ${skippedStages.length} stage(s)?`,
        content: (
          <div>
            <Text>Moving <Text strong>{job.job_number}</Text> from <Tag>{fromStage?.name}</Tag> to <Tag color="blue">{targetStage?.name}</Tag></Text>
            <Divider style={{ margin: "8px 0" }} />
            <Text type="warning">Stages to be skipped:</Text>
            <div style={{ marginTop: 4 }}>
              {skippedStages.map((s) => <Tag key={s} color="orange">{s}</Tag>)}
            </div>
          </div>
        ),
        okText: "Move & Skip",
        cancelText: "Cancel",
        onOk: () => doMove(),
        onCancel: fetchData, // revert
      });
    } else {
      await doMove();
    }
  };

  // ── Stage Completion Submit ────────────────────────────────
  const handleStageCompletionSubmit = async () => {
    try {
      const values = await stageForm.validateFields();
      const { job, to, targetStage, skippedStages } = pendingMove;

      const stageCompletion = {
        output_qty: values.output_qty || 0,
        wastage_qty: values.wastage_qty || 0,
        operator_name: values.operator_name || "",
        shift: values.shift || null,
        notes: values.notes || "",
      };

      // If skipping stages, show nested confirm
      const doMove = async () => {
        try {
          const result = await moveJobToStage(job.id, to, stageCompletion);
          toast.success(`${job.job_number} → ${targetStage?.name || to}`);
          if (result.skipped_stages?.length > 0) {
            toast.info(`Skipped: ${skippedStages.join(", ")}`);
          }
          setStageFormVisible(false);
          setPendingMove(null);
          stageForm.resetFields();
          fetchData();
        } catch (error) {
          toast.error(error.response?.data?.message || "Failed to move job");
          fetchData();
        }
      };

      if (skippedStages?.length > 0) {
        confirm({
          title: `Skip ${skippedStages.length} stage(s)?`,
          content: (
            <div>
              <Text type="warning">Stages to be skipped:</Text>
              <div style={{ marginTop: 4 }}>
                {skippedStages.map((s) => <Tag key={s} color="orange">{s}</Tag>)}
              </div>
            </div>
          ),
          okText: "Move & Skip",
          cancelText: "Cancel",
          onOk: doMove,
        });
      } else {
        await doMove();
      }
    } catch {
      // validation failed
    }
  };

  const handleStageCompletionCancel = () => {
    setStageFormVisible(false);
    setPendingMove(null);
    stageForm.resetFields();
    fetchData(); // revert optimistic move
  };

  // ── Dispatch ───────────────────────────────────────────────
  const openDispatchModal = async () => {
    setDispatchLoading(true);
    setDispatchModalVisible(true);
    try {
      const data = await getReadyForDispatch();
      setDispatchData(data);
    } catch (err) {
      toast.error("Failed to load dispatch data");
    }
    setDispatchLoading(false);
  };

  const handleDispatchSubmit = async () => {
    try {
      const values = await dispatchForm.validateFields();
      if (!dispatchItems.length || dispatchItems.every((i) => !i.qty_dispatched)) {
        return toast.error("Enter dispatch quantity for at least one job");
      }

      const payload = {
        customer_id: selectedCustomer,
        dispatch_date: values.dispatch_date?.toISOString() || new Date().toISOString(),
        receipt_type: values.receipt_type,
        receipt_number: values.receipt_number,
        delivery_type: values.delivery_type || "single",
        delivery_address: values.delivery_address,
        vehicle_number: values.vehicle_number,
        vehicle_name: values.vehicle_name,
        driver_name: values.driver_name,
        driver_phone: values.driver_phone,
        notes: values.notes,
        items: dispatchItems
          .filter((i) => i.qty_dispatched > 0)
          .map((i) => ({ job_id: i.job_id, qty_dispatched: i.qty_dispatched, notes: i.notes })),
      };

      setDispatchLoading(true);
      const result = await createDispatch(payload);
      toast.success(`Dispatch ${result.dispatch_number} created!`);
      setDispatchModalVisible(false);
      setSelectedCustomer(null);
      setDispatchItems([]);
      dispatchForm.resetFields();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create dispatch");
    } finally {
      setDispatchLoading(false);
    }
  };

  const handleCustomerSelect = (customerId) => {
    setSelectedCustomer(customerId);
    const custGroup = dispatchData?.customers?.find((c) => c.customer?.id === customerId);
    if (custGroup) {
      setDispatchItems(
        custGroup.jobs.map((j) => ({
          job_id: j.id,
          job_number: j.job_number,
          item_name: j.item_name,
          ordered_qty: j.ordered_qty,
          dispatched_qty: j.dispatched_qty,
          remaining_qty: j.remaining_qty,
          qty_dispatched: j.remaining_qty, // default to remaining
          notes: "",
        }))
      );
    } else {
      setDispatchItems([]);
    }
  };

  // ── Stats ──────────────────────────────────────────────────
  const totalCount = Object.values(columns).reduce(
    (sum, jobs) => sum + jobs.length,
    0
  );

  // ── Render ─────────────────────────────────────────────────
  return (
    <>
      <PageTitle title="Back" />
      <div className="kanban-container mr-top">
        {/* Header */}
        <Card bordered={false} style={{ marginBottom: 16 }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Space>
                <Title level={4} style={{ margin: 0 }}>
                  {flowName || "Production Kanban"}
                </Title>
                <Badge
                  count={totalCount}
                  showZero
                  color="#1890ff"
                  overflowCount={999}
                />
              </Space>
            </Col>
            <Col>
              <Space wrap>
                <Search
                  placeholder="Search jobs / orders..."
                  allowClear
                  style={{ width: 220 }}
                  onSearch={(value) =>
                    setFilters((f) => ({ ...f, search: value }))
                  }
                  prefix={<SearchOutlined />}
                />
                <Select
                  placeholder="Priority"
                  allowClear
                  style={{ width: 120 }}
                  value={filters.priority}
                  onChange={(value) =>
                    setFilters((f) => ({ ...f, priority: value }))
                  }
                >
                  <Select.Option value="urgent">Urgent</Select.Option>
                  <Select.Option value="high">High</Select.Option>
                  <Select.Option value="normal">Normal</Select.Option>
                  <Select.Option value="low">Low</Select.Option>
                </Select>
                <Select
                  placeholder="Material"
                  allowClear
                  style={{ width: 130 }}
                  value={filters.fulfillment_status}
                  onChange={(value) =>
                    setFilters((f) => ({ ...f, fulfillment_status: value }))
                  }
                >
                  <Select.Option value="fulfilled">Fulfilled</Select.Option>
                  <Select.Option value="partial">Partial</Select.Option>
                  <Select.Option value="pending">Pending</Select.Option>
                </Select>
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={openDispatchModal}
                >
                  Dispatch
                </Button>
                <Button
                  icon={<ReloadOutlined spin={loading} />}
                  onClick={fetchData}
                >
                  Refresh
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Board */}
        {loading && stages.length === 0 ? (
          <div style={{ textAlign: "center", padding: 60 }}>
            <Spin size="large" tip="Loading production board..." />
          </div>
        ) : stages.length === 0 ? (
          <Card style={{ textAlign: "center", padding: 40 }}>
            <Title level={5}>No production flow configured</Title>
            <Text type="secondary">
              Please create a default production flow in Settings to use the
              Kanban board.
            </Text>
          </Card>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            <div className="kanban-board">
              {stages.map((stage) => (
                <KanbanColumn
                  key={stage.code}
                  id={stage.code}
                  title={stage.name}
                  color={stage.color || "#1890ff"}
                  items={columns[stage.code] || []}
                  machineType={stage.machine_type}
                  requiresQC={stage.requires_qc}
                  onCardClick={(job) => setJobModalId(job.id)}
                />
              ))}
            </div>

            {/* Drag overlay for smooth animation */}
            <DragOverlay>
              {activeItem ? <KanbanCard item={activeItem} isDragging /> : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {/* Dispatch Modal */}
      <Modal
        title="Create Dispatch"
        open={dispatchModalVisible}
        onCancel={() => { setDispatchModalVisible(false); setSelectedCustomer(null); setDispatchItems([]); dispatchForm.resetFields(); }}
        onOk={handleDispatchSubmit}
        okText="Create Dispatch"
        confirmLoading={dispatchLoading}
        width={900}
        destroyOnClose
      >
        {dispatchLoading && !dispatchData ? (
          <Spin />
        ) : dispatchData?.total_jobs === 0 ? (
          <Text type="secondary">No jobs ready for dispatch.</Text>
        ) : (
          <Form form={dispatchForm} layout="vertical">
            {/* Customer selection */}
            <Form.Item label="Customer" required>
              <Select
                placeholder="Select customer"
                value={selectedCustomer}
                onChange={handleCustomerSelect}
                showSearch
                optionFilterProp="children"
              >
                {dispatchData?.customers?.map((c) => (
                  <Select.Option key={c.customer?.id} value={c.customer?.id}>
                    {c.customer?.name} ({c.jobs.length} jobs)
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            {/* Jobs table */}
            {dispatchItems.length > 0 && (
              <Table
                size="small"
                dataSource={dispatchItems}
                rowKey="job_id"
                pagination={false}
                bordered
                style={{ marginBottom: 16 }}
                columns={[
                  { title: "Job #", dataIndex: "job_number", width: 130 },
                  { title: "Item", dataIndex: "item_name", width: 180, ellipsis: true },
                  { title: "Ordered", dataIndex: "ordered_qty", width: 80 },
                  { title: "Dispatched", dataIndex: "dispatched_qty", width: 90 },
                  { title: "Remaining", dataIndex: "remaining_qty", width: 90,
                    render: (v) => <Text type={v > 0 ? "warning" : "success"}>{v}</Text> },
                  {
                    title: "Qty to Dispatch",
                    width: 120,
                    render: (_, record, idx) => (
                      <InputNumber
                        size="small"
                        min={0}
                        max={record.remaining_qty + 50} // allow some excess
                        value={record.qty_dispatched}
                        onChange={(val) => {
                          setDispatchItems((prev) => {
                            const next = [...prev];
                            next[idx] = { ...next[idx], qty_dispatched: val || 0 };
                            return next;
                          });
                        }}
                      />
                    ),
                  },
                  {
                    title: "Excess",
                    width: 70,
                    render: (_, r) => {
                      const excess = Math.max(0, (r.dispatched_qty + r.qty_dispatched) - r.ordered_qty);
                      return excess > 0 ? <Text type="danger">+{excess}</Text> : "-";
                    },
                  },
                ]}
              />
            )}

            <Row gutter={16}>
              <Col span={6}>
                <Form.Item name="dispatch_date" label="Dispatch Date" initialValue={moment()} rules={[{ required: true }]}>
                  <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="receipt_type" label="Receipt Type" rules={[{ required: true }]}>
                  <Radio.Group>
                    <Radio.Button value="challan">Challan</Radio.Button>
                    <Radio.Button value="invoice">Invoice</Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="receipt_number" label="Receipt Number" rules={[{ required: true }]}>
                  <Input placeholder="Challan / Invoice #" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="delivery_type" label="Delivery Type" initialValue="single">
                  <Select>
                    <Select.Option value="single">Single Point</Select.Option>
                    <Select.Option value="multi_point">Multi Point</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left" plain>Vehicle Details</Divider>
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item name="vehicle_number" label="Vehicle Number">
                  <Input placeholder="MH-12-XX-1234" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="vehicle_name" label="Vehicle / Transporter">
                  <Input placeholder="Transporter name" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="driver_name" label="Driver Name">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="driver_phone" label="Driver Phone">
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="delivery_address" label="Delivery Address">
              <Input.TextArea rows={2} placeholder="Leave blank to use customer default address" />
            </Form.Item>

            <Form.Item name="notes" label="Notes">
              <Input.TextArea rows={2} />
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Stage Completion Modal */}
      <Modal
        title={
          <Space>
            <CheckCircleOutlined style={{ color: "#52c41a" }} />
            <span>
              Stage Completion — {pendingMove?.fromStage?.name || pendingMove?.from}
            </span>
          </Space>
        }
        open={stageFormVisible}
        onCancel={handleStageCompletionCancel}
        onOk={handleStageCompletionSubmit}
        okText="Complete & Move"
        width={520}
        destroyOnClose
      >
        {pendingMove && (
          <div style={{ marginBottom: 16 }}>
            <Text>
              Moving <Text strong>{pendingMove.job?.job_number}</Text>{" "}
              from <Tag>{pendingMove.fromStage?.name}</Tag>{" "}
              to <Tag color="blue">{pendingMove.targetStage?.name}</Tag>
            </Text>
          </div>
        )}
        <Form form={stageForm} layout="vertical">
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="output_qty"
                label="Output Quantity"
                rules={[{ required: true, message: "Enter output qty" }]}
              >
                <InputNumber min={0} style={{ width: "100%" }} placeholder="Pcs produced" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="wastage_qty" label="Wastage / Rejected" initialValue={0}>
                <InputNumber min={0} style={{ width: "100%" }} placeholder="Rejected pcs" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="shift" label="Shift">
                <Select placeholder="Select shift" allowClear>
                  <Select.Option value="Morning">Morning</Select.Option>
                  <Select.Option value="Evening">Evening</Select.Option>
                  <Select.Option value="Night">Night</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="operator_name" label="Operator Name">
            <Input placeholder="Name of the operator" />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} placeholder="Any remarks for this stage..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Job Detail Modal */}
      <Modal
        open={!!jobModalId}
        onCancel={() => setJobModalId(null)}
        footer={null}
        destroyOnClose
        width="90vw"
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: "85vh", overflowY: "auto", padding: "12px 16px" }}
        title={null}
        closable
      >
        {jobModalId && (
          <JobDetail
            jobId={jobModalId}
            isModal
            onClose={() => {
              setJobModalId(null);
              fetchData();
            }}
          />
        )}
      </Modal>
    </>
  );
};

export default KanbanBoard;
